import React, { useEffect, useState } from 'react';
import { Dropdown, message, Spin } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { userManagementService, UserListItem } from './UserManagementService';
import styles from './UserManagement.module.css';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatLastActive(value: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Today · ${timeStr}`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return value;
  }
}

type RoleFilter = 'all' | 'admin' | 'application';

// ── UserManagement ─────────────────────────────────────────────────────────────

export const UserManagement: React.FC = () => {
  const [users, setUsers]           = useState<UserListItem[]>([]);
  const [loading, setLoading]       = useState(false);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');

  useEffect(() => {
    setLoading(true);
    userManagementService
      .getUsers()
      .then(data => setUsers(data))
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status !== 404) {
          message.error('Failed to load users');
        }
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const search = searchText.toLowerCase();
  const filtered = users.filter(u => {
    if (!u) return false;
    const matchesSearch =
      (u.fullName ?? '').toLowerCase().includes(search) ||
      (u.email ?? '').toLowerCase().includes(search);
    const roleStr = (u.role ?? '').toLowerCase();
    const matchesRole =
      roleFilter === 'all' ||
      (roleFilter === 'admin'       && roleStr.includes('admin')) ||
      (roleFilter === 'application' && !roleStr.includes('admin'));
    return matchesSearch && matchesRole;
  });

  const adminCount       = users.filter(u => (u?.role ?? '').toLowerCase().includes('admin')).length;
  const applicationCount = users.filter(u => !(u?.role ?? '').toLowerCase().includes('admin')).length;

  const rowMenu = (record: UserListItem): MenuProps['items'] => [
    {
      key: 'edit',
      label: 'Edit',
      onClick: ({ domEvent }) => { domEvent.stopPropagation(); message.info('Edit user coming soon'); },
    },
    {
      key: 'deactivate',
      label: 'Deactivate',
      danger: true,
      onClick: ({ domEvent }) => { domEvent.stopPropagation(); message.info(`Deactivate ${record.fullName} coming soon`); },
    },
  ];

  return (
    <div className={styles.pageWrap}>
      {/* Header */}
      <div className={styles.pageHead}>
        <h1 className={styles.pageTitle}>User Management</h1>
        <div className={styles.headActions}>
          <button
            className={styles.btnPrimary}
            onClick={() => message.info('Invite coming soon')}
          >
            <PlusOutlined /> Invite User
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className={styles.filterRow}>
        <div className={styles.searchWrap}>
          <SearchOutlined className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search users..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>
        <select
          className={styles.filterSelect}
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value as RoleFilter)}
        >
          <option value="all">All Users ({users.length})</option>
          <option value="admin">Admin Users ({adminCount})</option>
          <option value="application">Application Users ({applicationCount})</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className={styles.loadingState}><Spin /></div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th style={{ width: 180 }}>Role</th>
                <th style={{ width: 200 }}>Last Active</th>
                <th style={{ width: 48 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.emptyCell}>
                    {searchText ? 'No users match your search' : 'No users found'}
                  </td>
                </tr>
              ) : (
                filtered.map(record => (
                  <tr key={record.id} className={styles.tableRow}>
                    <td>
                      <div className={styles.cellName}>
                        {record.fullName}
                        {record.status === 'Inactive' && (
                          <span className={styles.badgeInactive}>Inactive</span>
                        )}
                      </div>
                      <div className={styles.cellEmail}>{record.email}</div>
                    </td>
                    <td>{record.role}</td>
                    <td style={{ color: 'var(--ce-text-muted)', fontSize: 12 }}>
                      {formatLastActive(record.lastActive)}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <Dropdown menu={{ items: rowMenu(record) }} trigger={['click']}>
                        <button className={styles.actionBtn} aria-label="Actions">
                          <MoreOutlined />
                        </button>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div className={styles.tableFooter}>
              Showing 1–{filtered.length} of {users.length} users
            </div>
          )}
        </div>
      )}
    </div>
  );
};
